import {
  Box,
  Disclaimer,
  DragScrollX,
  H3,
  ReceivablesSummary,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { SummaryGrid, SummarySlider } from './ReceivablesSummaryCards.styled';

type SummaryCard = {
  label: string;
  amount: number;
  color?: string;
  hint: string;
  onClick?: () => void;
};

type Props = {
  summary: ReceivablesSummary;
  /** Clicking "vDrive reserve used" lists the locations that used it. */
  onReserveUsed?: () => void;
};

/**
 * The headline numbers over the list. The reserve used and the item totals
 * are separate on purpose: the reserve is a negative balance the next top-up
 * or bank payment clears by itself, the items are debts somebody has to chase.
 */
export const ReceivablesSummaryCards = ({ summary, onReserveUsed }: Props) => {
  const locations =
    summary.locationsCount === 1
      ? '1 location'
      : `${summary.locationsCount} locations`;

  const cards: SummaryCard[] = [
    {
      label: 'Total owed',
      amount: summary.total,
      color: summary.total > 0 ? lightTheme.primary : lightTheme.darkGray,
      hint: locations,
    },
    {
      label: 'vDrive reserve used',
      amount: summary.vDriveOverdraft,
      hint: onReserveUsed ? 'See who used it' : 'Negative vDrive balances',
      onClick: onReserveUsed,
    },
    {
      label: 'Previous Transaction Due Balance',
      amount: summary.vDriveItems,
      hint: 'Debt from before the reserve',
    },
    {
      label: 'SaaS',
      amount: summary.saasItems,
      hint: 'Can be collected by bank only',
    },
    {
      label: 'On its way by bank',
      amount: summary.pendingBankTotal,
      color: lightTheme.darkYellow,
      hint: 'Bank payments in progress, fees included',
    },
  ];

  const boxes = cards.map((card) => (
    <Box
      key={card.label}
      $flex
      $column
      $gap="4px"
      $pointer={!!card.onClick}
      role={card.onClick ? 'button' : undefined}
      tabIndex={card.onClick ? 0 : undefined}
      onClick={card.onClick}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (card.onClick && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          card.onClick();
        }
      }}
    >
      <Disclaimer $m="0">{card.label}</Disclaimer>
      <H3 $m="0" $color={card.color}>
        {displayCentsInDollars(card.amount)}
      </H3>
      <Disclaimer $m="0" $color={lightTheme.lightGray}>
        {card.hint}
      </Disclaimer>
    </Box>
  ));

  // One row of five while they fit; below that a swipeable row, like the
  // Today cards on the dashboard, rather than a second row of one card.
  // The slider's negative margins let the shadows and the edge-to-edge
  // scroll out to the page padding without moving the layout.
  return (
    <>
      <SummaryGrid>{boxes}</SummaryGrid>
      <SummarySlider>
        <DragScrollX
          width="22%"
          widthMD="30%"
          m="-10px -50px -24px"
          p="10px 50px 24px"
          gap="16px"
          mSM="-10px -16px -24px"
          pSM="10px 16px 24px"
          gapSM="16px"
        >
          {boxes}
        </DragScrollX>
      </SummarySlider>
    </>
  );
};
